import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, NavLink, useLocation } from 'react-router-dom';
import '../styles/navbar.css';
import logo from '../assets/web-logo.png';
import { FaBars, FaTimes, FaChevronDown, FaTachometerAlt } from "react-icons/fa";
import { createPortal } from 'react-dom';
import { UserContext } from '../UserContext';

const projectsList = [
    { name: "Talent-Based Curriculum", path: "/talent-based-curriculum", id: "talent-based-curriculum" },
    { name: "Giant Teacher", path: "/giant-teacher", id: "giant-teacher" },
    { name: "Venting Room", path: "/venting-room", id: "venting-room" },
    { name: "Freelance", path: "/freelance", id: "freelance" },
    { name: "Entrepreneurship", path: "/entrepreneurship", id: "/entrepreneurship" },
    { name: "STEM Subjects", path: "/stem-subjects", id: "stem-subjects" },
];

const Navbar = () => {
    // Consume authentication state and functions from the UserContext
    const { isAuthenticated, userAccount, userStatus, logout } = useContext(UserContext); 
    
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNavActive, setIsNavActive] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();
    const location = useLocation();
    const navLinksRef = useRef(null);
    const dashboardButtonRef = useRef(null);
    const [isMainDashboardDropdownOpen, setIsMainDashboardDropdownOpen] = useState(false);

    // Get user details from the context, which is populated from the backend API
    const userName = userAccount?.name || '';
    const userSurname = userAccount?.surname || '';
    const userEmail = userAccount?.username || '';
    const userPlan = userStatus?.plan;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close mobile menu and dropdowns on location change
    useEffect(() => {
        setIsNavActive(false);
        setIsDropdownOpen(false);
        setIsMainDashboardDropdownOpen(false);
    }, [location.pathname]);

    const toggleNav = () => {
        setIsNavActive(!isNavActive);
        setIsDropdownOpen(false);
        setIsMainDashboardDropdownOpen(false);
    };

    const toggleMainDashboardDropdown = () => {
        setIsMainDashboardDropdownOpen(!isMainDashboardDropdownOpen);
        setIsDropdownOpen(false);
    };

    const closeMainDashboardDropdown = () => {
        setIsMainDashboardDropdownOpen(false);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
        setIsMainDashboardDropdownOpen(false);
    };

    // New simplified logout handler
    const handleLogout = async () => {
        try {
            await logout();
            // UserContext handles cleanup, we just need to navigate away
            navigate('/login');
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Helper: get dashboard route for the selected plan
    const getDashboardRoute = () => {
        switch (userPlan) {
            case 'venting':
                return '/venting-room-dashboard';
            case 'entrepreneurship':
                return '/entrepreneurship-education-dashboard';
            case 'stem':
                return '/stem-subjects-dashboard';
            case 'bundled':
                return '/bundled-dashboard';
            default:
                return '/checkout';
        }
    };

    const renderNavLinks = () => {
        const dashboardRoute = getDashboardRoute();

        // Determine if a dashboard link should be active based on current location
        const isDashboardActive = location.pathname.includes('/dashboard') || location.pathname.includes('/checkout');
        const isProjectActive = projectsList.some(project => location.pathname.startsWith(project.path));
        const isResearchActive = location.pathname === '/research';

        const navLinks = (
            <ul className={`nav-links ${isAuthenticated ? 'authenticated' : ''} ${isDashboardActive ? 'dashboard-active' : ''} ${isNavActive ? 'active' : ''}`} ref={navLinksRef}>
                {isNavActive && (
                    <div className="close-icon" onClick={toggleNav}>
                        <FaTimes size={20} />
                    </div>
                )}
                <li><NavLink to="/" onClick={toggleNav} className={({ isActive }) => (isActive ? "active" : "")} end>Home</NavLink></li>
                <li><NavLink to="/about" onClick={toggleNav} className={({ isActive }) => (isActive ? "active" : "")}>About Us</NavLink></li>
                <li><NavLink to="/research" onClick={toggleNav} className={({ isActive }) => (isResearchActive ? "active" : "")}>Research</NavLink></li>
                <li className={`dropdown ${isDropdownOpen ? 'open' : ''} ${isProjectActive ? 'active' : ''}`}>
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            toggleDropdown();
                        }}
                        className="dropdown-trigger"
                    >
                        Projects <FaChevronDown className="dropdown-icon" />
                    </div>
                    <ul className={`dropdown-menu ${isDropdownOpen ? 'open' : ''}`}>
                        {projectsList.map((project) => (
                            <li key={project.path}>
                                <NavLink
                                    to={project.path}
                                    onClick={() => { toggleNav(); setIsDropdownOpen(false); }}
                                    className={({ isActive }) => (isActive ? "active" : "")}
                                >
                                    {project.name}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </li>
                <li><NavLink to="/contact" onClick={toggleNav} className={({ isActive }) => (isActive ? "active" : "")}>Contact Us</NavLink></li>
                {isAuthenticated ? (
                    <>
                        <li className={`dashboard-link ${isMainDashboardDropdownOpen ? 'open' : ''} ${isDashboardActive ? 'active' : ''}`} ref={dashboardButtonRef}>
                            <div className="main-dashboard-container" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <Link to={dashboardRoute} onClick={toggleNav} className={`dropdown-trigger ${isDashboardActive ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'white', fontWeight: 'bold' }}>
                                    <FaTachometerAlt size={20} className="dashboard-icon" style={{ color: 'white' }} />
                                    <span className="dashboard-text" style={{ marginLeft: 6, color: 'white' }}>Main Dashboard</span>
                                </Link>
                                <FaChevronDown
                                    className="dropdown-icon"
                                    style={{ marginLeft: 6, color: 'white' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleMainDashboardDropdown();
                                    }}
                                />
                            </div>
                            {isMainDashboardDropdownOpen && (
                                <div className="dashboard-dropdown-menu">
                                    <div className="dropdown-header">
                                        <span><b>{userName} {userSurname}</b></span>
                                        <FaTimes size={20} className="close-icon" onClick={closeMainDashboardDropdown} style={{ cursor: 'pointer' }} />
                                    </div>
                                    <ul className="dropdown-list" style={{ listStyleType: 'none', padding: 0 }}>
                                        <li>
                                            <p>{userEmail}</p>
                                        </li>
                                        <li>
                                            <Link to={dashboardRoute} onClick={toggleNav}>Go to Dashboard</Link>
                                        </li>
                                        <li>
                                            <button className="logout-button" onClick={handleLogout}>Logout</button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </li>
                    </>
                ) : (
                    <li><Link to="/signup" className="join-us-button" onClick={toggleNav}>Join Us</Link></li>
                )}
            </ul>
        );
        if (isNavActive && isMobile) {
            return createPortal(navLinks, document.body);
        } else {
            return navLinks;
        }
    };

    return (
        <>
            <nav className="navbar">
                <div className="navbar-left">
                    <Link to="/">
                        <img src={logo} alt="Logo" className="logo" />
                    </Link>
                </div>
                <div className="menu-icon" onClick={toggleNav}>
                    {isNavActive ? <FaTimes size={30} /> : <FaBars size={30} />}
                </div>
                {!isMobile && renderNavLinks()}
            </nav>
            {isMobile && renderNavLinks()}
        </>
    );
};

export default Navbar;
